// Shared analytics computation utilities
// Used by both client-side (live events) and referenced by Cloud Functions logic

import type { SongRequest, AttendeeRanking } from '../services/requests';
import type { Event } from '../services/events';
import { attendeeDisplayName } from './attendeeDisplayName';
import type {
  EventAnalytics,
  AnalyticsSummary,
  QueueMetrics,
  DiscoveryMetrics,
  TimelineBucket,
  PeakMetrics,
  AttendeeProfile,
  VoteRecord,
} from '../types/analytics';

// ============================================
// GENRE INFERENCE
// ============================================

export const ARTIST_GENRE_MAP: Record<string, string> = {
  // Pop
  'taylor swift': 'Pop',
  'ed sheeran': 'Pop',
  'dua lipa': 'Pop',
  'justin timberlake': 'Pop',
  'pharrell williams': 'Pop',
  'the weeknd': 'Pop',
  'bts': 'K-Pop',
  'ariana grande': 'Pop',
  'billie eilish': 'Pop',
  'bruno mars': 'Pop',
  'harry styles': 'Pop',
  'olivia rodrigo': 'Pop',
  'sabrina carpenter': 'Pop',
  'charlie puth': 'Pop',
  // Rock
  'queen': 'Rock',
  'ac/dc': 'Rock',
  'led zeppelin': 'Rock',
  'nirvana': 'Rock',
  'foo fighters': 'Rock',
  'imagine dragons': 'Rock',
  'twenty one pilots': 'Rock',
  // Hip-Hop/Rap
  'drake': 'Hip-Hop',
  'kendrick lamar': 'Hip-Hop',
  'eminem': 'Hip-Hop',
  'travis scott': 'Hip-Hop',
  'post malone': 'Hip-Hop',
  'kanye west': 'Hip-Hop',
  'j. cole': 'Hip-Hop',
  // Electronic/Dance
  'daft punk': 'Electronic',
  'calvin harris': 'Electronic',
  'marshmello': 'Electronic',
  'tones and i': 'Dance',
  'avicii': 'Electronic',
  'tiesto': 'Electronic',
  // R&B
  'beyonce': 'R&B',
  'usher': 'R&B',
  'sza': 'R&B',
  // Country
  'luke bryan': 'Country',
  'carrie underwood': 'Country',
  'morgan wallen': 'Country',
  'luke combs': 'Country',
  // Latin
  'bad bunny': 'Latin',
  'shakira': 'Latin',
  'j balvin': 'Latin',
};

export function inferGenre(artist: string): string {
  const normalizedArtist = artist.toLowerCase().trim();
  return ARTIST_GENRE_MAP[normalizedArtist] || 'Other';
}

export const GENRE_COLORS: Record<string, string> = {
  'Pop': '#6366F1',
  'K-Pop': '#EC4899',
  'Rock': '#EF4444',
  'Hip-Hop': '#F59E0B',
  'Electronic': '#8B5CF6',
  'Dance': '#10B981',
  'R&B': '#3B82F6',
  'Country': '#F97316',
  'Latin': '#14B8A6',
  'Other': '#6B7280',
};

// ============================================
// FORMAT HELPERS
// ============================================

export function formatDuration(ms?: number): string {
  if (!ms) return '--:--';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function generateCSV(requests: SongRequest[], _eventName: string): string {
  const headers = [
    'Song Title', 'Artist', 'Duration', 'Status', 'Vote Count',
    'Upvotes', 'Downvotes', 'Requester Nickname', 'Message', 'Explicit',
    'Spotify URI', 'Requested At', 'Approved At', 'Played At',
  ];

  const rows = requests.map(r => [
    `"${r.song.title.replace(/"/g, '""')}"`,
    `"${r.song.artist.replace(/"/g, '""')}"`,
    formatDuration(r.song.durationMs),
    r.status,
    r.voteCount.toString(),
    r.upvoteCount.toString(),
    r.downvoteCount.toString(),
    `"${attendeeDisplayName(r.requestedBy.nickname, r.requestedBy.oderId).replace(/"/g, '""')}"`,
    r.message ? `"${r.message.replace(/"/g, '""')}"` : '',
    r.song.explicitFlag ? 'Yes' : 'No',
    r.spotifyUri || '',
    r.timestamps.requested ? new Date(r.timestamps.requested).toLocaleString() : '',
    r.timestamps.approved ? new Date(r.timestamps.approved).toLocaleString() : '',
    r.timestamps.finishedPlaying ? new Date(r.timestamps.finishedPlaying).toLocaleString() : '',
  ]);

  return [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
}

// ============================================
// CORE ANALYTICS COMPUTATION
// ============================================

export function computeEventAnalytics(
  requests: SongRequest[],
  votes: VoteRecord[],
  rankings: AttendeeRanking[],
  event: Event,
): Omit<EventAnalytics, 'computedAt'> {
  const summary = computeSummary(requests, votes, rankings, event);
  const queue = computeQueueMetrics(requests);
  const discovery = computeDiscoveryMetrics(requests, votes);
  const timeline = computeTimeline(requests, votes);
  const peaks = computePeaks(timeline, votes);

  return {
    eventId: event.id,
    djId: event.djId,
    eventName: event.name,
    eventDate: event.date,
    summary,
    queue,
    discovery,
    timeline,
    peaks,
    attendees: { totalSessions: 0, avgSessionDurationMinutes: 0, deviceBreakdown: {} },
  };
}

function computeSummary(
  requests: SongRequest[],
  votes: VoteRecord[],
  rankings: AttendeeRanking[],
  event: Event,
): AnalyticsSummary {
  const totalUpvotes = votes.filter(v => v.type === 'up').length;
  const totalDownvotes = votes.filter(v => v.type === 'down').length;
  const totalVotes = totalUpvotes + totalDownvotes;

  const uniqueRequesters = new Set(requests.map(r => r.requestedBy.oderId)).size;
  const uniqueVoters = new Set(votes.map(v => v.oderId)).size;
  const uniqueRankers = new Set(rankings.map(r => r.attendeeId)).size;
  const uniqueAttendees = new Set([
    ...requests.map(r => r.requestedBy.oderId),
    ...votes.map(v => v.oderId),
    ...rankings.map(r => r.attendeeId),
  ]).size;

  // Event duration from start/end times
  let eventDurationMinutes = 0;
  if (event.startTime && event.endTime) {
    const [sh, sm] = event.startTime.split(':').map(Number);
    const [eh, em] = event.endTime.split(':').map(Number);
    let mins = (eh * 60 + em) - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60; // past midnight
    eventDurationMinutes = mins;
  }

  return {
    totalRequests: requests.length,
    totalVotes,
    totalUpvotes,
    totalDownvotes,
    uniqueAttendees,
    uniqueRequesters,
    uniqueVoters,
    uniqueRankers,
    avgRequestsPerAttendee: uniqueRequesters > 0
      ? Math.round((requests.length / uniqueRequesters) * 10) / 10
      : 0,
    avgVotesPerAttendee: uniqueVoters > 0
      ? Math.round((totalVotes / uniqueVoters) * 10) / 10
      : 0,
    eventDurationMinutes,
    engagementRate: uniqueAttendees > 0
      ? Math.round(((uniqueRequesters + uniqueVoters) / uniqueAttendees) * 50) // average of request + vote rates
      : 0,
  };
}

function computeQueueMetrics(requests: SongRequest[]): QueueMetrics {
  const totalQueued = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying').length;
  const totalPlayed = requests.filter(r => r.status === 'played').length;
  const totalRejected = requests.filter(r => r.status === 'rejected').length;
  const totalPending = requests.filter(r => r.status === 'pending').length;
  const total = requests.length;

  // Time-to-queue: requested → approved
  const queueTimes: number[] = [];
  const playTimes: number[] = [];
  for (const r of requests) {
    if (r.timestamps.requested && r.timestamps.approved) {
      const diff = new Date(r.timestamps.approved).getTime() - new Date(r.timestamps.requested).getTime();
      if (diff > 0) queueTimes.push(diff);
    }
    if (r.timestamps.approved && r.timestamps.startedPlaying) {
      const diff = new Date(r.timestamps.startedPlaying).getTime() - new Date(r.timestamps.approved).getTime();
      if (diff > 0) playTimes.push(diff);
    }
  }

  const contentFilteredCount = requests.filter(r =>
    r.status === 'rejected' && r.song.explicitFlag
  ).length;

  const superRequestCount = requests.filter(r => r.superRequest).length;

  return {
    totalQueued,
    totalPlayed,
    totalRejected,
    totalPending,
    requestSuccessRate: total > 0
      ? Math.round(((totalQueued + totalPlayed) / total) * 100)
      : 0,
    avgTimeToQueueMs: queueTimes.length > 0
      ? Math.round(queueTimes.reduce((a, b) => a + b, 0) / queueTimes.length)
      : 0,
    avgTimeToPlayMs: playTimes.length > 0
      ? Math.round(playTimes.reduce((a, b) => a + b, 0) / playTimes.length)
      : 0,
    contentFilteredCount,
    superRequestCount,
  };
}

function computeDiscoveryMetrics(requests: SongRequest[], votes: VoteRecord[]): DiscoveryMetrics {
  // Songs with Spotify URI that were played = estimated streams
  const playedWithSpotify = requests.filter(r =>
    r.spotifyUri && (r.status === 'played' || r.status === 'nowPlaying')
  );
  const songsWithSpotifyUri = requests.filter(r => r.spotifyUri).length;

  // Genre distribution
  const genreDistribution: Record<string, number> = {};
  for (const r of requests) {
    const genre = inferGenre(r.song.artist);
    genreDistribution[genre] = (genreDistribution[genre] || 0) + 1;
  }

  const topGenre = Object.entries(genreDistribution)
    .sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

  // Unique song titles (discovery = new songs being introduced)
  const uniqueSongs = new Set(requests.map(r => `${r.song.title}|${r.song.artist}`.toLowerCase()));
  const discoveryRate = requests.length > 0
    ? Math.round((uniqueSongs.size / requests.length) * 100)
    : 0;

  // Cross-genre engagement: attendees who voted on songs from 3+ genres
  const attendeeGenreMap = new Map<string, Set<string>>();
  for (const v of votes) {
    const request = requests.find(r => r.id === v.requestId);
    if (request) {
      const genre = inferGenre(request.song.artist);
      if (!attendeeGenreMap.has(v.oderId)) attendeeGenreMap.set(v.oderId, new Set());
      attendeeGenreMap.get(v.oderId)!.add(genre);
    }
  }
  const crossGenreEngagement = Array.from(attendeeGenreMap.values())
    .filter(genres => genres.size >= 3).length;

  return {
    songsWithSpotifyUri,
    estimatedStreams: playedWithSpotify.length,
    discoveryRate,
    genreDistribution,
    topGenre,
    crossGenreEngagement,
  };
}

export function computeTimeline(requests: SongRequest[], votes: VoteRecord[]): TimelineBucket[] {
  const buckets: Record<string, TimelineBucket> = {};

  const getBucket = (dateStr: string) => {
    const date = new Date(dateStr);
    const h = date.getHours().toString().padStart(2, '0');
    const m = (Math.floor(date.getMinutes() / 15) * 15).toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  const ensureBucket = (key: string) => {
    if (!buckets[key]) {
      buckets[key] = { bucket: key, requests: 0, votes: 0, upvotes: 0, downvotes: 0 };
    }
  };

  for (const r of requests) {
    if (!r.timestamps.requested) continue;
    const key = getBucket(r.timestamps.requested);
    ensureBucket(key);
    buckets[key].requests++;
  }

  for (const v of votes) {
    if (!v.votedAt) continue;
    const key = getBucket(v.votedAt);
    ensureBucket(key);
    buckets[key].votes++;
    if (v.type === 'up') buckets[key].upvotes++;
    else buckets[key].downvotes++;
  }

  return Object.values(buckets).sort((a, b) => a.bucket.localeCompare(b.bucket));
}

export function computePeaks(timeline: TimelineBucket[], votes: VoteRecord[]): PeakMetrics {
  let peakRequestBucket = '';
  let peakVoteBucket = '';
  let peakCombinedBucket = '';
  let maxReq = 0;
  let maxVote = 0;
  let maxCombined = 0;

  for (const b of timeline) {
    if (b.requests > maxReq) { maxReq = b.requests; peakRequestBucket = b.bucket; }
    if (b.votes > maxVote) { maxVote = b.votes; peakVoteBucket = b.bucket; }
    if (b.requests + b.votes > maxCombined) { maxCombined = b.requests + b.votes; peakCombinedBucket = b.bucket; }
  }

  // Votes per hour: group votes by hour
  const votesByHour: Record<number, number> = {};
  for (const v of votes) {
    if (!v.votedAt) continue;
    const hour = new Date(v.votedAt).getHours();
    votesByHour[hour] = (votesByHour[hour] || 0) + 1;
  }
  const hourCounts = Object.values(votesByHour);
  const votesPerHour = hourCounts.length > 0
    ? Math.round(hourCounts.reduce((a, b) => a + b, 0) / hourCounts.length)
    : 0;
  const maxVotesPerHour = hourCounts.length > 0 ? Math.max(...hourCounts) : 0;

  return { peakRequestBucket, peakVoteBucket, peakCombinedBucket, votesPerHour, maxVotesPerHour };
}

export function withLiveTimeline(
  saved: EventAnalytics,
  requests: SongRequest[],
  votes: VoteRecord[],
): EventAnalytics {
  const timeline = computeTimeline(requests, votes);
  return { ...saved, timeline, peaks: computePeaks(timeline, votes) };
}

// ============================================
// ATTENDEE PROFILE COMPUTATION
// ============================================

export function computeAttendeeProfiles(
  requests: SongRequest[],
  votes: VoteRecord[],
  rankings: AttendeeRanking[],
  verifiedOderIds: Set<string>,
): AttendeeProfile[] {
  // Build nickname map
  const nicknameMap = new Map<string, string>();
  for (const r of requests) {
    if (r.requestedBy.nickname && r.requestedBy.oderId) {
      nicknameMap.set(r.requestedBy.oderId, r.requestedBy.nickname);
    }
  }

  // All unique attendee IDs
  const allIds = new Set<string>();
  requests.forEach(r => { if (r.requestedBy.oderId) allIds.add(r.requestedBy.oderId); });
  votes.forEach(v => { if (v.oderId) allIds.add(v.oderId); });
  rankings.forEach(r => { if (r.attendeeId) allIds.add(r.attendeeId); });

  const profiles: AttendeeProfile[] = [];

  for (const attendeeId of allIds) {
    const attendeeRequests = requests.filter(r => r.requestedBy.oderId === attendeeId);
    const requestCount = attendeeRequests.length;

    const attendeeVotes = votes.filter(v => v.oderId === attendeeId);
    const upvoteCount = attendeeVotes.filter(v => v.type === 'up').length;
    const downvoteCount = attendeeVotes.filter(v => v.type === 'down').length;

    const attendeeRankings = rankings.filter(r => r.attendeeId === attendeeId);
    const rankingCount = attendeeRankings.reduce((sum, r) => sum + r.rankings.length, 0);

    // Timestamps for active window
    const timestamps: string[] = [];
    attendeeRequests.forEach(r => { if (r.timestamps.requested) timestamps.push(r.timestamps.requested); });
    attendeeVotes.forEach(v => { if (v.votedAt) timestamps.push(v.votedAt); });
    attendeeRankings.forEach(r => { if (r.updatedAt) timestamps.push(r.updatedAt); });
    const sorted = timestamps.filter(Boolean).sort();

    // Top genre
    const genreCounts: Record<string, number> = {};
    attendeeRequests.forEach(r => {
      const genre = inferGenre(r.song.artist);
      genreCounts[genre] = (genreCounts[genre] || 0) + 1;
    });
    const topGenre = Object.entries(genreCounts).sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A';

    const engagementScore = requestCount * 3 + (upvoteCount + downvoteCount) * 1 + rankingCount * 2;

    profiles.push({
      attendeeId,
      nickname: nicknameMap.get(attendeeId) || attendeeDisplayName(null, attendeeId),
      requestCount,
      upvoteCount,
      downvoteCount,
      rankingCount,
      firstActiveTime: sorted[0] || '',
      lastActiveTime: sorted[sorted.length - 1] || '',
      topGenre,
      engagementScore,
      isVerified: verifiedOderIds.has(attendeeId),
    });
  }

  return profiles.sort((a, b) => b.engagementScore - a.engagementScore);
}

// ============================================
// GENRE SORTED DATA FOR CHARTS
// ============================================

export function computeGenreChartData(requests: SongRequest[]) {
  const genreDistribution = requests.reduce((acc, r) => {
    const genre = inferGenre(r.song.artist);
    acc[genre] = (acc[genre] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(genreDistribution)
    .sort(([, a], [, b]) => b - a)
    .map(([genre, count]) => ({
      genre,
      count,
      percentage: requests.length > 0 ? Math.round((count / requests.length) * 100) : 0,
      color: GENRE_COLORS[genre] || GENRE_COLORS['Other'],
    }));
}
